import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useStore } from '../state/store.tsx';
import { todayDay } from './clock.ts';
import {
  currentFromOverview, forecastResult, forecastView, goals, monthView, overview, ownedAssets, plannedAssets, restOfMonthFromOverview, type ForecastView,
} from './model.ts';

export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const mql = window.matchMedia(query);
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    },
    [query],
  );
  return useSyncExternalStore(subscribe, () => window.matchMedia(query).matches, () => false);
}

export const DESKTOP_QUERY = '(min-width: 1024px)';
export const useIsDesktop = () => useMediaQuery(DESKTOP_QUERY);
export const useReducedMotion = () => useMediaQuery('(prefers-reduced-motion: reduce)');

/** Width of an element, tracked with ResizeObserver. */
export function useWidth<T extends HTMLElement>(): [React.RefObject<T | null>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.getBoundingClientRect().width);
    const observer = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w !== undefined) setWidth(w);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

export function useForecastView(years: number) {
  const { plan, waypoints } = useStore();
  const today = todayDay();
  return useMemo(() => (plan ? forecastView(plan, waypoints, today, years) : null), [plan, waypoints, today, years]);
}

export type FlowYear = ForecastView['flowYears'][number];

/** The month bars by calendar year, from views' forecast screen (R22). */
export const useFlowYears = (view: ForecastView | null): readonly FlowYear[] => view?.flowYears ?? [];

/** The overview result get_overview returns for the same plan, waypoints and day. */
export function useOverview() {
  const { plan, waypoints } = useStore();
  const today = todayDay();
  return useMemo(() => (plan ? overview(plan, waypoints, today) : null), [plan, waypoints, today]);
}

export function useForecastResult(years: number) {
  const { plan, waypoints } = useStore();
  const today = todayDay();
  return useMemo(() => (plan ? forecastResult(plan, waypoints, today, { years }) : null), [plan, waypoints, today, years]);
}

export function useGoalsResult(years: number) {
  const { plan, waypoints } = useStore();
  const today = todayDay();
  return useMemo(() => (plan ? goals(plan, waypoints, today, years) : null), [plan, waypoints, today, years]);
}

export function useHomeData() {
  const { plan, waypoints } = useStore();
  const years = plan?.configuration.timeframeYears ?? 10;
  const forecast = useForecastView(years);
  const result = useOverview();
  const today = todayDay();
  return useMemo(() => {
    if (!plan || !forecast || !result) return null;
    const current = currentFromOverview(result);
    return {
      view: { ...forecast, current },
      overview: result,
      month: monthView(plan, waypoints, today, current.cash),
      restOfMonth: restOfMonthFromOverview(result),
      assets: ownedAssets(plan, today),
      planned: plannedAssets(plan, today),
    };
  }, [plan, waypoints, forecast, result, today]);
}

/** Persist a small UI preference in localStorage (never plan data). */
export function useLocalPreference<T>(key: string, initial: T): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw === null ? initial : (JSON.parse(raw) as T);
    } catch {
      return initial;
    }
  });
  const set = useCallback(
    (next: T) => {
      setValue(next);
      try {
        localStorage.setItem(key, JSON.stringify(next));
      } catch {
        /* storage may be unavailable */
      }
    },
    [key],
  );
  return [value, set];
}

export function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(id);
  }, [value, ms]);
  return debounced;
}
